// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ProfileFrameCommands from '@app/features/user/commands/ProfileFrameCommands';
import {rewriteLegacyApiHostUrl} from '@app/features/user/utils/LegacyApiHostRewrite';
import type {ProfileFrameCatalogEntryResponse} from '@fluxer/schema/src/domains/user/ProfileFrameSchemas';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('ProfileFrameCatalog');
const CATALOG_CLIENT_TTL_MS = 5_000;

let inflightLoad: Promise<void> | null = null;

class ProfileFrameCatalog {
	frames: Array<ProfileFrameCatalogEntryResponse> = [];
	loaded = false;
	loading = false;
	freshUntil = 0;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	getById(id: string | null | undefined): ProfileFrameCatalogEntryResponse | undefined {
		if (!id) {
			return undefined;
		}
		return this.frames.find((entry) => entry.id === id);
	}

	async load(force = false): Promise<void> {
		if (!force && this.loaded && this.frames.length > 0 && Date.now() < this.freshUntil) {
			return;
		}
		if (inflightLoad) {
			return inflightLoad;
		}
		inflightLoad = this.fetchCatalog();
		try {
			await inflightLoad;
		} finally {
			inflightLoad = null;
		}
	}

	private async fetchCatalog(): Promise<void> {
		this.loading = true;
		try {
			const response = await ProfileFrameCommands.fetchProfileFrameCatalog();
			runInAction(() => {
				this.frames = response.profile_frames.map((entry) => ({
					...entry,
					top: rewriteLegacyApiHostUrl(entry.top),
					bottom: entry.bottom == null ? entry.bottom : rewriteLegacyApiHostUrl(entry.bottom),
					thumbnail: entry.thumbnail == null ? entry.thumbnail : rewriteLegacyApiHostUrl(entry.thumbnail),
				}));
				this.loaded = true;
				this.freshUntil = Date.now() + CATALOG_CLIENT_TTL_MS;
			});
		} catch (error) {
			logger.warn('Failed to load profile frame catalog', error);
			runInAction(() => {
				this.frames = [];
				this.loaded = true;
				this.freshUntil = Date.now() + CATALOG_CLIENT_TTL_MS;
			});
		} finally {
			runInAction(() => {
				this.loading = false;
			});
		}
	}
}

export default new ProfileFrameCatalog();
